import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PlayIcon, PauseIcon, PlusIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8005';

export default function PodcastLibrary() {
  const { user } = useAuth();
  const [podcasts, setPodcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playingId, setPlayingId] = useState(null);

  useEffect(() => {
    loadPodcasts();
  }, [user]);

  const loadPodcasts = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      // Fetch all podcast sessions for this user
      const response = await axios.get(`${API_URL}/podcast/sessions/${user.id}`);
      setPodcasts(response.data.sessions || []);
    } catch (err) {
      console.error('Failed to load podcasts:', err);
    } finally {
      setLoading(false);
    }
  };

  const togglePlay = (podcastId) => {
    if (playingId === podcastId) {
      setPlayingId(null);
    } else {
      setPlayingId(podcastId);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-8">
        <div className="text-center">Loading...</div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-8 mb-8 border border-purple-100">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            <span className="text-5xl">🎥️</span>
          </div>
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900 mb-3">Your Life Story, Preserved Forever</h1>
            <p className="text-lg text-gray-700 mb-4">
              These aren't just recordings—they're your legacy. Each podcast captures your stories, wisdom, and voice in a way that will comfort and inspire your loved ones for generations.
            </p>
            <div className="bg-white/60 backdrop-blur rounded-lg p-4 mb-4">
              <h3 className="font-semibold text-gray-900 mb-2">What makes these special:</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start">
                  <span className="text-purple-600 mr-2">🎯</span>
                  <span>AI-guided interviews that draw out your most meaningful memories</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-600 mr-2">💝</span>
                  <span>Your authentic voice, preserved with all its warmth and personality</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-600 mr-2">✨</span>
                  <span>Stories and advice that will guide your family long after you're gone</span>
                </li>
              </ul>
            </div>
            <Link
              to="/podcast"
              className="inline-flex items-center px-6 py-3 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-purple-600 hover:bg-purple-700 transition-colors"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Record Your Story
            </Link>
          </div>
        </div>
      </div>

      {/* Podcasts List */}
      {podcasts.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-12 text-center">
          <div className="w-24 h-24 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-5xl">🎥️</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Your Story Awaits</h2>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            Start your first podcast interview. Share the stories only you can tell—the moments that shaped you, the lessons you've learned, the love you want to pass on.
          </p>
          <Link
            to="/podcast"
            className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-bold text-lg hover:from-purple-700 hover:to-pink-700 shadow-lg transition-all"
          >
            <PlusIcon className="h-6 w-6 mr-2" />
            Start Your First Podcast
          </Link>
        </div>
      ) : (
        <div className="space-y-6">
          {podcasts.map((podcast) => (
            <div
              key={podcast.id}
              className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-all"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  {/* Status Badge */}
                  <div className="flex items-center gap-3 mb-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      podcast.status === 'completed' 
                        ? 'bg-green-100 text-green-800'
                        : podcast.status === 'in_progress'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {podcast.status === 'completed' ? '✓ Completed' : 
                       podcast.status === 'in_progress' ? '⏳ In Progress' : 
                       '📝 Draft'}
                    </span>
                    {podcast.voice_id && (
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">
                        🎤 Voice Cloned
                      </span>
                    )}
                  </div>

                  {/* Podcast Info */}
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    Podcast Interview - {formatDate(podcast.created_at)}
                  </h3>
                  
                  <div className="flex items-center gap-6 text-sm text-gray-600 mb-4">
                    <span>📅 {formatDate(podcast.created_at)}</span>
                    {podcast.duration && (
                      <span>⏱️ {formatDuration(podcast.duration)}</span>
                    )}
                    {podcast.recordings && (
                      <span>💬 {podcast.recordings.length} questions answered</span>
                    )}
                  </div>

                  {/* Audio Player */}
                  {podcast.final_podcast_url && (
                    <div className="bg-gray-50 rounded-lg p-4 mb-4">
                      <audio
                        controls
                        src={`${API_URL}${podcast.final_podcast_url}`}
                        className="w-full"
                        onPlay={() => setPlayingId(podcast.id)}
                        onPause={() => setPlayingId(null)}
                      />
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-3">
                    {podcast.final_podcast_url && (
                      <a
                        href={`${API_URL}${podcast.final_podcast_url}`}
                        download={`podcast-${formatDate(podcast.created_at)}.mp3`}
                        className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition-all"
                      >
                        <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                        Download
                      </a>
                    )}
                    
                    {podcast.status === 'in_progress' && (
                      <Link
                        to="/podcast"
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-all"
                      >
                        Continue Recording
                      </Link>
                    )}

                    {podcast.voice_id && (
                      <Link
                        to="/voice-clone"
                        className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium transition-all"
                      >
                        View Voice Clone
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info Section */}
      <div className="mt-12 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border-2 border-blue-200">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">💡 About Your Podcasts</h2>
        <div className="space-y-3 text-gray-700">
          <p>
            <strong>Voice Cloning:</strong> When you complete a podcast, we automatically clone your voice from the recording. This voice is used in all personalized messages.
          </p>
          <p>
            <strong>Editing:</strong> You can continue in-progress podcasts or record a new one anytime. Each completed podcast creates a voice clone.
          </p>
          <p>
            <strong>Sharing:</strong> Download your completed podcasts to share with family, or use them as the source for your AI voice messages.
          </p>
        </div>
      </div>
    </div>
  );
}
